import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TooltipModule } from 'primeng/tooltip';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslocoRootModule } from 'src/app/transloco-root.module';
import { TranslocoService } from '@ngneat/transloco';
import { MessageService } from 'primeng/api';
import { InvoiceService } from 'src/app/service/invoice.service';
import { InvoiceDetail, NfStatus } from 'src/app/model/response/invoice-response';
import { saveNfseXml } from 'src/app/shared/nfse/nfse-download.util';

@Component({
  selector: 'app-invoice-detail-modal',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, TagModule, ProgressSpinnerModule, TooltipModule, TranslocoRootModule],
  templateUrl: './invoice-detail-modal.component.html',
  styleUrl: './invoice-detail-modal.component.css',
})
export class InvoiceDetailModalComponent implements OnChanges {
  @Input() visible: boolean = false;
  @Input() invoiceId: string | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() notFound = new EventEmitter<void>();

  invoice: InvoiceDetail | null = null;
  loading: boolean = false;
  loadError: boolean = false;
  downloadingNf: boolean = false;

  constructor(
    private invoiceService: InvoiceService,
    private translocoService: TranslocoService,
    private messageService: MessageService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && this.invoiceId) {
      this.loadDetail(this.invoiceId);
    }
    if (changes['visible']?.currentValue === false) {
      this.invoice = null;
      this.loadError = false;
    }
  }

  loadDetail(id: string): void {
    this.loading = true;
    this.loadError = false;
    this.invoice = null;
    this.invoiceService.getInvoiceDetail(id).subscribe({
      next: (data) => {
        this.invoice = data;
        this.loading = false;
      },
      error: (err: HttpErrorResponse) => {
        this.loading = false;
        if (err.status === 404) {
          this.close();
          this.notFound.emit();
        } else {
          this.loadError = true;
        }
      },
    });
  }

  nfStatusSeverity(status: NfStatus): 'secondary' | 'warning' | 'info' | 'success' | 'danger' {
    const map: Record<NfStatus, 'secondary' | 'warning' | 'info' | 'success' | 'danger'> = {
      not_applicable: 'secondary',
      pending: 'warning',
      requested: 'info',
      issued: 'success',
      failed: 'danger',
    };
    return map[status];
  }

  nfStatusLabel(status: NfStatus): string {
    return this.translocoService.translate(`user.account.subscriptionTab.invoices.nfStatus.${status}`);
  }

  displayName(invoice: InvoiceDetail): string {
    return invoice.tokenPackageID ? (invoice.tokenPackageName ?? '') : invoice.planName;
  }

  typeLabel(invoice: InvoiceDetail): string {
    return this.translocoService.translate(
      invoice.tokenPackageID
        ? 'user.account.subscriptionTab.invoices.typeTokenPurchase'
        : 'user.account.subscriptionTab.invoices.typeSubscription'
    );
  }

  documentLabel(): string {
    const billing = this.invoice?.billing;
    if (!billing?.document) return '';
    const type = billing.documentType ? ` (${billing.documentType.toUpperCase()})` : '';
    return `${billing.document}${type}`;
  }

  formattedAddress(): string {
    const billing = this.invoice?.billing;
    if (!billing) return '';
    const cityState = [billing.city, billing.state].filter(Boolean).join('/');
    return [billing.addressLine1, billing.addressLine2, cityState, billing.postalCode, billing.country]
      .filter(Boolean)
      .join(', ');
  }

  openReceipt(): void {
    if (this.invoice?.stripeReceiptUrl) {
      window.open(this.invoice.stripeReceiptUrl, '_blank');
    }
  }

  /** Abre a representação imprimível da DPS em outra aba, fora do layout do dashboard. */
  viewNf(): void {
    if (this.invoice?.nfStatus === 'issued') {
      window.open(`/invoices/${this.invoice.id}/nfse`, '_blank');
    }
  }

  /** A rota exige o header Authorization, então o download passa pelo HttpClient (blob). */
  downloadNfXml(): void {
    const id = this.invoice?.id;
    if (!id || this.invoice?.nfStatus !== 'issued' || this.downloadingNf) return;
    this.downloadingNf = true;
    this.invoiceService.downloadNfXml(id).subscribe({
      next: (blob) => {
        this.downloadingNf = false;
        saveNfseXml(blob, id);
      },
      error: (err: HttpErrorResponse) => {
        this.downloadingNf = false;
        const key = err.status === 404
          ? 'user.account.subscriptionTab.invoices.nfNotAvailable'
          : 'user.account.subscriptionTab.invoices.nfDownloadError';
        this.messageService.add({
          severity: err.status === 404 ? 'warn' : 'error',
          summary: this.translocoService.translate(key),
        });
      },
    });
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
